<template>
  <div>
    <div
      class="d-flex flex-wrap justify-content-between align-items-start mb-2"
    >
      <div>
        <p class="mb-1">
          <strong>{{ permissionName }}</strong> ·
          {{ $t("label.permissionDefinitionLevel") }} {{ level }} —
          {{ levelName }}
        </p>
        <p class="small text-muted">{{ permissionKey }}</p>
      </div>
      <b-button
        variant="outline-info"
        class="mb-2"
        :disabled="loading || exporting || bulkBusy"
        @click="exportUsers"
        ><b-spinner v-if="exporting" small class="mr-1" /><i
          v-else
          class="mdi mdi-download mr-1"
          aria-hidden="true"
        ></i
        >{{ $t("label.permissionDefinitionExport") }}</b-button
      >
    </div>
    <b-alert v-if="exportError" show variant="danger">{{
      exportError
    }}</b-alert>
    <b-input-group class="mb-3">
      <b-form-input
        v-model.trim="search"
        :disabled="bulkBusy"
        type="search"
        :placeholder="$t('label.search')"
        :aria-label="$t('label.search')"
        @keyup.enter="applySearch"
      />
      <b-input-group-append
        ><b-button
          variant="info"
          :disabled="bulkBusy"
          :aria-label="$t('label.search')"
          @click="applySearch"
          ><i class="mdi mdi-magnify"></i></b-button
      ></b-input-group-append>
    </b-input-group>
    <div v-if="canBulkUpdate" class="border rounded p-3 mb-3">
      <div class="d-flex flex-wrap align-items-center">
        <span class="mr-3 mb-2">{{
          $t("label.permissionBulkSelected", [selectedUuids.length])
        }}</span>
        <b-form-select
          v-model="targetLevel"
          :options="targetOptions"
          :disabled="bulkBusy || loading"
          class="bulk-level mr-2 mb-2"
          :aria-label="$t('label.permissionBulkChooseLevel')"
        />
        <b-button
          variant="info"
          class="mb-2"
          :disabled="
            bulkBusy ||
            loading ||
            !selectedUuids.length ||
            targetLevel == null ||
            targetLevel === level
          "
          @click="bulkUpdate(false)"
          ><b-spinner v-if="bulkBusy" small class="mr-1" />{{
            $t("label.permissionBulkUpdate")
          }}</b-button
        >
        <b-button
          variant="outline-info"
          class="ml-2 mb-2"
          :disabled="bulkBusy || loading || targetLevel == null"
          @click="bulkUpdate(true)"
          >{{ $t("label.permissionBulkUpdateAll") }}</b-button
        >
      </div>
      <small class="text-muted">{{ $t("label.permissionBulkPageHint") }}</small>
    </div>
    <b-alert v-if="bulkError" show variant="danger">{{ bulkError }}</b-alert>
    <div v-if="loading" class="text-center p-5">
      <b-spinner variant="info" />
    </div>
    <b-alert v-else-if="error" show variant="danger"
      >{{ $t("label.permissionDefinitionUsersError") }}
      <b-button size="sm" variant="outline-danger" @click="loadUsers">{{
        $t("label.retryPermissionDefinitions")
      }}</b-button></b-alert
    >
    <template v-else>
      <b-table
        :items="users"
        :fields="fields"
        primary-key="uuid"
        responsive
        show-empty
      ><template #cell(company)="{ item }"><CompanyLink :uuid="item.companyUUID" :name="item.company" /></template>
        <template #cell(name)="{ item }">
          <router-link v-if="item.uuid" :to="{ name: 'UserDetails', params: { userUUID: item.uuid } }">{{ item.name }}</router-link>
          <span v-else>{{ item.name }}</span>
        </template>
        <template #head(selected)
          ><b-form-checkbox
            :checked="allSelected"
            :indeterminate="pageSelectedCount > 0 && !allSelected"
            :disabled="bulkBusy || !users.length"
            :aria-label="$t('label.permissionBulkSelectPage')"
            @change="selectPage"
        /></template>
        <template #cell(selected)="{ item }"
          ><b-form-checkbox
            :checked="selectedUuids.includes(item.uuid)"
            :disabled="bulkBusy || !item.uuid"
            :aria-label="$t('label.permissionBulkSelectUser', [item.name])"
            @change="selectUser(item.uuid, $event)"
        /></template>
      </b-table>
      <div
        v-if="total"
        class="d-flex flex-wrap justify-content-between align-items-center mt-3"
      >
        <small class="text-muted">{{
          $tc("label.results", total, [total])
        }}</small>
        <b-pagination
          :disabled="bulkBusy"
          :value="page"
          :total-rows="total"
          :per-page="pageSize"
          class="mb-0"
          @change="changePage"
        />
      </div>
    </template>
  </div>
</template>
<script>
import { sharedHelper, sessionHelper, alertHelper } from "@/utils";
import { userPermissionDefinitionService } from "@/services/api/userPermissionDefinition.service";
export default {
  name: "UsersByPermission",
  props: {
    permissionKey: { type: String, required: true },
    level: { type: Number, required: true },
    permissionName: { type: String, default: "" },
    levelName: { type: String, default: "" },
    levelOptions: { type: Array, default: () => [] },
  },
  data() {
    return {
      users: [],
      search: "",
      searchWords: [],
      page: 1,
      pageSize: 15,
      total: 0,
      loading: true,
      error: false,
      requestId: 0,
      exporting: false,
      exportError: "",
      exportRequestId: 0,
      downloadURL: "",
      downloadTimer: null,
      selectedUuids: [],
      targetLevel: null,
      bulkBusy: false,
      bulkError: "",
      bulkRequestId: 0,
    };
  },
  computed: {
    canBulkUpdate() {
      const user = this.$store.state.api.auth.user?.user;
      return (
        sessionHelper.getUserPermissions(user?.permissions, user?.isSuperadmin)
          .user_admin === 2
      );
    },
    targetOptions() {
      return [
        {
          value: null,
          text: this.$t("label.permissionBulkChooseLevel"),
          disabled: true,
        },
        ...this.levelOptions.map((option) => ({
          value: option.value,
          text: `${option.value} — ${option.text}`,
        })),
      ];
    },
    pageSelectedCount() {
      return this.users.filter((user) => this.selectedUuids.includes(user.uuid))
        .length;
    },
    allSelected() {
      return (
        this.users.length > 0 &&
        this.users.every((user) => this.selectedUuids.includes(user.uuid))
      );
    },
    fields() {
      return [
        ...(this.canBulkUpdate ? [{ key: "selected", label: "" }] : []),
        { key: "name", label: this.$t("label.name") },
        { key: "email", label: this.$t("label.email") },
        { key: "phone", label: this.$t("label.phone") },
        { key: "company", label: this.$t("label.company") },
        { key: "position", label: this.$t("module.position") },
      ];
    },
  },
  watch: {
    canBulkUpdate(allowed) {
      if (!allowed) {
        this.selectedUuids = [];
        this.targetLevel = null;
      }
    },
  },
  created() {
    this.loadUsers();
  },
  beforeDestroy() {
    this.requestId++;
    this.exportRequestId++;
    this.bulkRequestId++;
    this.clearDownload();
  },
  methods: {
    selectPage(selected) {
      if (this.bulkBusy || sessionHelper.getPermissionLevel("user_admin") !== 2)
        return;
      this.selectedUuids = selected
        ? this.users.map((user) => user.uuid).filter(Boolean)
        : [];
    },
    selectUser(uuid, selected) {
      if (
        this.bulkBusy ||
        !uuid ||
        sessionHelper.getPermissionLevel("user_admin") !== 2
      )
        return;
      this.selectedUuids = selected
        ? [...new Set([...this.selectedUuids, uuid])]
        : this.selectedUuids.filter((value) => value !== uuid);
    },
    async bulkUpdate(selectAll = false) {
      if (
        typeof selectAll !== "boolean" ||
        this.bulkBusy ||
        this.loading ||
        sessionHelper.getPermissionLevel("user_admin") !== 2 ||
        (!selectAll &&
          (!this.selectedUuids.length || this.targetLevel === this.level)) ||
        !this.levelOptions.some((option) => option.value === this.targetLevel)
      )
        return;
      const request = ++this.bulkRequestId,
        userUUIDs = [...this.selectedUuids],
        target = this.targetLevel;
      const targetLabel = this.levelOptions.find(
        (option) => option.value === target,
      ).text;
      this.bulkBusy = true;
      this.bulkError = "";
      try {
        const confirmed = await this.$bvModal.msgBoxConfirm(
          selectAll
            ? this.$t("label.permissionBulkConfirmAll", [
                this.permissionName,
                target,
                targetLabel,
              ])
            : this.$t("label.permissionBulkConfirm", [
                userUUIDs.length,
                this.permissionName,
                target,
                targetLabel,
              ]),
          {
            title: this.$t(
              selectAll
                ? "label.permissionBulkUpdateAll"
                : "label.permissionBulkUpdate",
            ),
            okTitle: this.$t("label.confirm"),
            cancelTitle: this.$t("label.cancel"),
            okVariant: "info",
            centered: true,
          },
        );
        if (
          !confirmed ||
          request !== this.bulkRequestId ||
          sessionHelper.getPermissionLevel("user_admin") !== 2
        )
          return;
        const result = await userPermissionDefinitionService.bulkUpdate(
          this.permissionKey,
          target,
          selectAll ? [] : userUUIDs,
          selectAll,
        );
        if (request !== this.bulkRequestId) return;
        if (!result?.success)
          throw new Error(
            result?.message || this.$t("label.permissionBulkError"),
          );
        alertHelper.alertSuccess({
          message: this.$t("label.permissionBulkSuccess"),
        });
        this.selectedUuids = [];
        this.targetLevel = null;
        await this.loadUsers();
        if (!this.error && !this.users.length && this.page > 1) {
          this.page = Math.max(1, Math.ceil(this.total / this.pageSize));
          await this.loadUsers();
        }
      } catch (error) {
        if (request === this.bulkRequestId)
          this.bulkError =
            sharedHelper.getErrorMessage(error) ||
            this.$t("label.permissionBulkError");
      } finally {
        if (request === this.bulkRequestId) this.bulkBusy = false;
      }
    },
    clearDownload() {
      clearTimeout(this.downloadTimer);
      if (this.downloadURL) URL.revokeObjectURL(this.downloadURL);
      this.downloadURL = "";
      this.downloadTimer = null;
    },
    async exportUsers() {
      if (
        this.bulkBusy ||
        this.exporting ||
        Math.max(
          sessionHelper.getPermissionLevel("developer"),
          sessionHelper.getPermissionLevel("user_admin"),
        ) < 1
      )
        return;
      const request = ++this.exportRequestId;
      this.exporting = true;
      this.exportError = "";
      try {
        const blob = await userPermissionDefinitionService.exportUsers(
          this.permissionKey,
          this.level,
          { searchWords: [...this.searchWords] },
        );
        if (
          request !== this.exportRequestId ||
          Math.max(
            sessionHelper.getPermissionLevel("developer"),
            sessionHelper.getPermissionLevel("user_admin"),
          ) < 1
        )
          return;
        this.clearDownload();
        const type = blob.type.toLowerCase();
        const extension =
          type.includes("csv") || type.includes("text/plain")
            ? "csv"
            : type.includes("spreadsheetml")
            ? "xlsx"
            : type.includes("ms-excel")
            ? "xls"
            : type.includes("pdf")
            ? "pdf"
            : "xlsx";
        this.downloadURL = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = this.downloadURL;
        link.download = `users-${this.permissionKey}-level-${this.level}.${extension}`;
        document.body.appendChild(link);
        try {
          link.click();
        } finally {
          link.remove();
        }
        this.downloadTimer = setTimeout(() => this.clearDownload(), 30000);
      } catch (error) {
        if (request === this.exportRequestId) {
          this.clearDownload();
          this.exportError =
            sharedHelper.getErrorMessage(error) ||
            this.$t("label.permissionDefinitionExportError");
        }
      } finally {
        if (request === this.exportRequestId) this.exporting = false;
      }
    },
    applySearch() {
      if (this.bulkBusy) return;
      this.selectedUuids = [];
      this.searchWords = this.search ? [this.search] : [];
      this.page = 1;
      return this.loadUsers();
    },
    changePage(page) {
      if (this.bulkBusy) return;
      this.selectedUuids = [];
      this.page = page;
      return this.loadUsers();
    },
    async loadUsers() {
      this.selectedUuids = [];
      const request = ++this.requestId;
      this.loading = true;
      this.error = false;
      try {
        const result = await userPermissionDefinitionService.getUsers(
          this.permissionKey,
          this.level,
          {
            page: this.page,
            size: this.pageSize,
            searchWords: this.searchWords,
          },
        );
        if (!result?.success || !Array.isArray(result.data))
          throw new Error("Invalid user list.");
        if (request !== this.requestId) return;
        this.users = result.data.map((user) => ({
          uuid: user.uuid,
          name:
            user.full_name ||
            user.name ||
            [user.first_name, user.last_name].filter(Boolean).join(" ") ||
            "-",
          email: user.email || "-",
          phone: user.phone
            ? [user.phone_code, user.phone].filter(Boolean).join(" ")
            : "-",
          companyUUID: user.primary_company?.uuid || null,
          company: user.primary_company?.name || "-",
          position: user.position?.name || "-",
        }));
        this.total = result.pagination?.total ?? result.data.length;
        this.page = result.pagination?.current_page || this.page;
        this.pageSize = result.pagination?.per_page || this.pageSize;
      } catch (error) {
        if (request === this.requestId) this.error = true;
      } finally {
        if (request === this.requestId) this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.bulk-level {
  width: auto;
  max-width: 100%;
  min-width: 220px;
}
</style>
